import React, { useCallback, useEffect, useState } from 'react'
import {
	Box,
	Checkbox,
	Collapse,
	IconButton,
	Typography,
} from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import _ from 'lodash'
import { getConnexionName } from 'src/utils/externalIntegration'
import { pluralize } from 'src/utils/stringUtils'
import {
	useCampaignModalSkeletonStyles,
	G,
	GL,
	CAMPAIGN_ICON_TEXT_GAP_PX,
} from '../CampaignModalSkeleton.styles'
import { SHOPS_MODAL_TEXT } from '../../../../constants/marketingCampaign'

export interface RestaurantsStepProps {
	shops: Record<string, any>
	selectedShopIds: string[]
	onSelectedShopIdsChange: (ids: string[]) => void
}

export function RestaurantsStep({
	shops,
	selectedShopIds,
	onSelectedShopIdsChange,
}: RestaurantsStepProps) {
	const classes = useCampaignModalSkeletonStyles()
	const [expanded, setExpanded] = useState<Record<string, boolean>>({})
	const [selectedGroup, setSelectedGroup] = useState<string | null>(null)
	const [manuallySelectedGroups, setManuallySelectedGroups] = useState<string[]>([])

	const shopsWithIndex = _.map(shops, (shop, index) => ({
		...shop,
		index: String(index),
	}))
	const groupedShops = _.groupBy(shopsWithIndex, (shop) =>
		getConnexionName(shop, shop?.index, null),
	)

	useEffect(() => {
		const fullySelectedGroup = Object.keys(groupedShops).find((groupName) => {
			const groupShopIds = groupedShops[groupName].map((shop) => shop.index)
			return groupShopIds.every((id) => selectedShopIds.includes(id))
		})

		if (fullySelectedGroup && !manuallySelectedGroups.includes(fullySelectedGroup)) {
			setSelectedGroup(fullySelectedGroup)
		}
	}, [selectedShopIds, groupedShops, manuallySelectedGroups])

	const handleToggleExpand = useCallback((groupName: string) => {
		setExpanded((e) => ({ ...e, [groupName]: !e[groupName] }))
	}, [])

	const handleShopToggle = useCallback(
		(shopId: string, groupName: string, groupDisabled: boolean) => {
			if (groupDisabled) {
				return
			}
			const next = selectedShopIds.includes(shopId)
				? selectedShopIds.filter((id) => id !== shopId)
				: [...selectedShopIds, shopId]

			if (selectedGroup === groupName && !next.includes(shopId)) {
				setSelectedGroup(null)
			}
			onSelectedShopIdsChange(next)
		},
		[onSelectedShopIdsChange, selectedGroup, selectedShopIds],
	)

	const handleGroupToggle = useCallback(
		(groupName: string, groupDisabled: boolean) => {
			if (groupDisabled) {
				return
			}
			const groupShopIds = groupedShops[groupName].map((shop) => shop.index)
			const allSelected = groupShopIds.every((id) => selectedShopIds.includes(id))

			if (allSelected) {
				setSelectedGroup(null)
				setManuallySelectedGroups((prev) => prev.filter((g) => g !== groupName))
				onSelectedShopIdsChange(
					selectedShopIds.filter((id) => !groupShopIds.includes(id)),
				)
			} else {
				setSelectedGroup(groupName)
				setManuallySelectedGroups((prev) => [...prev, groupName])
				onSelectedShopIdsChange(_.uniq([...selectedShopIds, ...groupShopIds]))
			}
		},
		[groupedShops, onSelectedShopIdsChange, selectedShopIds],
	)

	const isGroupSelected = useCallback(
		(groupName: string) => {
			const groupShopIds = groupedShops[groupName].map((shop) => shop.index)
			return groupShopIds.every((id) => selectedShopIds.includes(id))
		},
		[groupedShops, selectedShopIds],
	)

	const isGroupDisabled = useCallback(
		(groupName: string) => {
			return (
				(selectedGroup !== null && selectedGroup !== groupName) ||
				(selectedShopIds.length > 0 &&
					!selectedShopIds.some((shopId) =>
						groupedShops[groupName].some((shop) => shop.index === shopId),
					))
			)
		},
		[groupedShops, selectedGroup, selectedShopIds],
	)

	const totalShops = _.size(shops)
	const selectedCount = selectedShopIds.length

	return (
		<Box>
			<Typography variant="body2" style={{ color: '#616161', marginBottom: 14, lineHeight: 1.65 }}>
				{SHOPS_MODAL_TEXT}
			</Typography>
			{selectedShopIds.length > 0 && (
				<Box
					display="flex"
					alignItems="center"
					gridGap={CAMPAIGN_ICON_TEXT_GAP_PX}
					style={{
						padding: '9px 14px',
						background: GL,
						border: `1px solid ${G}`,
						borderRadius: 10,
						marginBottom: 12,
					}}
				>
					<CheckIcon style={{ color: G, fontSize: 18 }} />
					<Typography variant="body2" style={{ color: G, fontWeight: 500 }}>
						{selectedCount} restaurant{pluralize(selectedCount)} sélectionné
						{pluralize(selectedCount)}
					</Typography>
				</Box>
			)}
			<Typography variant="body2" style={{ fontWeight: 500, color: '#212121', marginBottom: 8 }}>
				Restaurants ciblés
			</Typography>
			{Object.keys(groupedShops).map((groupName, index) => {
				const group = groupName.split('-')[0]
				const version = groupName.split('-')[1]
				const groupShops = groupedShops[groupName]
				const sel = isGroupSelected(groupName)
				const groupDisabled = isGroupDisabled(groupName)
				const exp = expanded[groupName]

				return (
					<Box key={groupName} mb={exp ? 0 : 0.625}>
						<Box
							className={classes.rowGreen}
							onClick={() =>
								!groupDisabled && handleGroupToggle(groupName, groupDisabled)
							}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: CAMPAIGN_ICON_TEXT_GAP_PX,
								padding: '10px 12px',
								border: `1px solid ${sel && !groupDisabled ? G : 'rgba(0,0,0,0.15)'}`,
								borderRadius: exp ? '10px 10px 0 0' : 10,
								marginBottom: exp ? 0 : 5,
								cursor: groupDisabled ? 'not-allowed' : 'pointer',
								background:
									sel && !groupDisabled ? 'rgba(0, 153, 115, 0.04)' : '#fff',
								opacity: groupDisabled ? 0.55 : 1,
								transition: 'all .12s',
							}}
						>
							<Checkbox
								checked={sel}
								color="primary"
								disabled={groupDisabled}
								onClick={(e) => e.stopPropagation()}
								onChange={() =>
									!groupDisabled && handleGroupToggle(groupName, groupDisabled)
								}
								style={{ padding: 0 }}
							/>
							<Typography variant="body2" style={{ fontWeight: 500, flex: 1 }}>
								Groupe {index + 1}
							</Typography>
							<Typography variant="caption" style={{ color: '#9e9e9e' }}>
								{groupShops.length}/{totalShops} restaurants
							</Typography>
							<Box style={{ textAlign: 'right', minWidth: 130 }}>
								<Typography variant="caption" display="block" style={{ color: '#757575' }}>
									Intégration: {group}
								</Typography>
								{version && (
									<Typography variant="caption" display="block" style={{ color: '#9e9e9e' }}>
										Version de menu {version}
									</Typography>
								)}
							</Box>
							<IconButton
								size="small"
								disabled={groupDisabled}
								onClick={(e) => {
									e.stopPropagation()
									handleToggleExpand(groupName)
								}}
							>
								<ExpandMoreIcon
									style={{
										transform: exp ? 'rotate(180deg)' : 'none',
										transition: 'transform .2s',
									}}
								/>
							</IconButton>
						</Box>
						<Collapse in={Boolean(exp)}>
							<Box
								style={{
									border: `1px solid ${sel && !groupDisabled ? G : 'rgba(0,0,0,0.12)'}`,
									borderTop: 'none',
									borderRadius: '0 0 10px 10px',
									marginBottom: 5,
									background:
										sel && !groupDisabled ? 'rgba(0, 153, 115, 0.02)' : '#fafafa',
								}}
							>
								{groupShops.map((shop, i) => (
									<Box
										key={shop.index}
										display="flex"
										alignItems="center"
										gridGap={CAMPAIGN_ICON_TEXT_GAP_PX}
										style={{
											padding: '8px 12px 8px 42px',
											borderBottom:
												i < groupShops.length - 1
													? '1px solid rgba(0,0,0,0.06)'
													: 'none',
										}}
									>
										<Checkbox
											checked={selectedShopIds.includes(shop.index)}
											disabled={groupDisabled}
											color="primary"
											onChange={() =>
												handleShopToggle(shop.index, groupName, groupDisabled)
											}
											style={{ padding: 0 }}
										/>
										<Typography variant="body2" style={{ color: '#424242' }}>
											{shop.name}
										</Typography>
									</Box>
								))}
							</Box>
						</Collapse>
					</Box>
				)
			})}
		</Box>
	)
}
